import {
  getRedPacketRecords,
  exportRedPacketRecordsCsv,
  RedPacketRecord,
  RedPacketRecordFilter,
  RedPacketStatus,
  RedPacketType,
  ThresholdTier,
  ThresholdTierType,
} from '../../../services/redpacketService';
import {
  Datagrid,
  FunctionField,
  NumberInput,
  required,
  TextField,
  TextInput,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  Grid,
  MenuItem,
  Stack,
  TextField as MuiTextField,
  Typography,
  TablePagination,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Chip from '@mui/material/Chip';
import { useCallback, useEffect, useState } from 'react';
import { Dayjs } from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import { PermissionButton } from 'src/routes/components/permission-button';
import { downloadBlob } from 'src/utils/format';

const RecordsForm = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [data, setData] = useState<RedPacketRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [filter, setFilter] = useState<RedPacketRecordFilter>({});
  const [filterValues, setFilterValues] = useState({
    userId: '',
    walletAddress: '',
    status: [] as RedPacketStatus[],
    types: [] as RedPacketType[],
    createdFrom: null as Dayjs | null,
    createdTo: null as Dayjs | null,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (pagination.page - 1) * pagination.perPage;
      const result = await getRedPacketRecords(
        filter,
        { limit: pagination.perPage, offset },
        { field: 'CREATED_AT', direction: 'DESC' }
      );
      setData(Array.isArray(result.records) ? result.records : []);
      setTotal(result.totalCount || 0);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, filter, notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTypeName = (type: RedPacketType) => {
    const typeMap: Record<RedPacketType, string> = {
      [RedPacketType.TWITTER_BINDING]: 'TWITTER_BINDING',
      [RedPacketType.TWITTER_FOLLOW]: 'TWITTER_FOLLOW',
      [RedPacketType.DEPOSIT]: 'DEPOSIT',
      [RedPacketType.TRADING]: 'TRADING',
      [RedPacketType.CRITICAL_HIT]: 'CRITICAL_HIT',
    };
    return typeMap[type] || type;
  };

  const getStatusName = (status: RedPacketStatus) => {
    const statusMap: Record<RedPacketStatus, string> = {
      [RedPacketStatus.PENDING]: 'PENDING',
      [RedPacketStatus.CLAIMED]: 'CLAIMED',
      [RedPacketStatus.EXPIRED]: 'EXPIRED',
    };
    return statusMap[status] || status;
  };

  const handleFilterChange = (field: string, value: any) => {
    if (field === 'types') {
      // 处理多选类型，确保值是 RedPacketType 数组
      let selectedTypes: RedPacketType[];
      if (Array.isArray(value)) {
        selectedTypes = value;
      } else if (typeof value === 'string') {
        selectedTypes = value.split(',').filter(Boolean) as RedPacketType[];
      } else {
        selectedTypes = value ? [value] : [];
      }
      setFilterValues((prev) => ({ ...prev, [field]: selectedTypes }));
    } else {
      setFilterValues((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleApplyFilter = () => {
    const newFilter: RedPacketRecordFilter = {
      userId: filterValues.userId || undefined,
      walletAddress: filterValues.walletAddress || undefined,
      types: filterValues.types.length > 0 ? filterValues.types : undefined,
      statuses: filterValues.status.length > 0 ? filterValues.status : undefined,
      createdFrom: filterValues.createdFrom
        ? `${filterValues.createdFrom.format('YYYY-MM-DD')}T00:00:00.000Z`
        : undefined,
      createdTo: filterValues.createdTo ? `${filterValues.createdTo.format('YYYY-MM-DD')}T23:59:59.999Z` : undefined,
    };
    setFilter(newFilter);
    setPagination({ page: 1, perPage: pagination.perPage });
  };

  const buildFilterFromFormValues = useCallback((): RedPacketRecordFilter => {
    return {
      userId: filterValues.userId || undefined,
      walletAddress: filterValues.walletAddress || undefined,
      types: filterValues.types.length > 0 ? filterValues.types : undefined,
      statuses: filterValues.status.length > 0 ? filterValues.status : undefined,
      createdFrom: filterValues.createdFrom
        ? `${filterValues.createdFrom.format('YYYY-MM-DD')}T00:00:00.000Z`
        : undefined,
      createdTo: filterValues.createdTo ? `${filterValues.createdTo.format('YYYY-MM-DD')}T23:59:59.999Z` : undefined,
    };
  }, [filterValues]);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai';
      const { blob, filename } = await exportRedPacketRecordsCsv({
        // 注意：导出必须以“当前表单筛选值”为准，避免用户选了日期/状态但没点搜索导致条件丢失
        filter: buildFilterFromFormValues(),
        sort: { field: 'CREATED_AT', direction: 'DESC' },
        timezone,
      });
      downloadBlob(filename, blob);
      notify(translate('module.redpacket.exportSuccess', { _: 'Export success' }) as any, { type: 'info' });
    } catch (e: any) {
      notify(e?.message || translate('module.redpacket.exportFailed', { _: 'Export failed' }), { type: 'warning' });
    } finally {
      setExporting(false);
    }
  }, [exporting, buildFilterFromFormValues, notify, translate]);

  return (
    <Card sx={{ mt: 3, pb: 2 }}>
      {/* 自定义筛选表单 */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
           <MuiTextField
                fullWidth
                label={translate('module.redpacket.loginAccount')}
                value={filterValues.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MuiTextField
                fullWidth
                label={translate('module.redpacket.rewardAddressCol')}
                value={filterValues.walletAddress}
                onChange={(e) => handleFilterChange('walletAddress', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MuiTextField
                fullWidth
                select
                label={translate('module.redpacket.types')}
                value={filterValues.types}
                onChange={(e) => {
                  handleFilterChange('types', e.target.value);
                }}
                size="small"
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: any) => {
                    if (!Array.isArray(selected) || selected.length === 0) {
                      return 'ALL';
                    }
                    const typeNames: Record<RedPacketType, string> = {
                      [RedPacketType.TWITTER_BINDING]: 'TWITTER_BINDING',
                      [RedPacketType.TWITTER_FOLLOW]: 'TWITTER_FOLLOW',
                      [RedPacketType.DEPOSIT]: 'DEPOSIT',
                      [RedPacketType.TRADING]: 'TRADING',
                      [RedPacketType.CRITICAL_HIT]: 'CRITICAL_HIT',
                    };
                    return selected
                      .map((type) => typeNames[type as RedPacketType] || type)
                      .join(', ');
                  },
                }}
              >
                <MenuItem value={RedPacketType.TWITTER_BINDING}>TWITTER_BINDING</MenuItem>
                <MenuItem value={RedPacketType.TWITTER_FOLLOW}>TWITTER_FOLLOW</MenuItem>
                <MenuItem value={RedPacketType.DEPOSIT}>DEPOSIT</MenuItem>
                <MenuItem value={RedPacketType.TRADING}>TRADING</MenuItem>
                <MenuItem value={RedPacketType.CRITICAL_HIT}>CRITICAL_HIT</MenuItem>
              </MuiTextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MuiTextField
                fullWidth
                select
                label={translate('module.redpacket.status')}
                value={filterValues.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: any) => {
                    if (!Array.isArray(selected) || selected.length === 0) {
                      return 'ALL';
                    }
                    const statusNames: Record<RedPacketStatus, string> = {
                      [RedPacketStatus.PENDING]: 'PENDING',
                      [RedPacketStatus.CLAIMED]: 'CLAIMED',
                      [RedPacketStatus.EXPIRED]: 'EXPIRED',
                    };
                    return selected
                      .map((type) => statusNames[type as RedPacketStatus] || type)
                      .join(', ');
                  },
                }}
              >
                <MenuItem value={RedPacketStatus.PENDING}>PENDING</MenuItem>
                <MenuItem value={RedPacketStatus.CLAIMED}>CLAIMED</MenuItem>
                <MenuItem value={RedPacketStatus.EXPIRED}>EXPIRED</MenuItem>
              </MuiTextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={translate('module.redpacket.startTime')}
                value={filterValues.createdFrom}
                onChange={(value) => handleFilterChange('createdFrom', value)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={translate('module.redpacket.endTime')}
                value={filterValues.createdTo}
                onChange={(value) => handleFilterChange('createdTo', value)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={handleApplyFilter}>
                  {translate('resource.search')}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilterValues({
                      userId: '',
                      walletAddress: '',
                      status: [],
                      types: [],
                      createdFrom: null,
                      createdTo: null,
                    });
                    setFilter({});
                  }}
                >
                  {translate('module.redpacket.reset')}
                </Button>
                </Stack>

                <PermissionButton resource="redpacket" action="export">
                  <Button
                    variant="contained"
                    onClick={handleExport}
                    disabled={exporting}
                    startIcon={exporting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : null}
                    sx={{ textTransform: 'none' }}
                  >
                    {translate('module.redpacket.csvExport', { _: 'CSV Export' })}
                  </Button>
                </PermissionButton>
              </Stack>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Box sx={{ overflow: 'auto' }}>
        <Datagrid
          rowClick={false}
          data={data}
          isLoading={loading}
          total={total}
          bulkActionButtons={false}
        >
          <TextField source="userId" label={translate('module.redpacket.loginAccount')} />
          <TextField source="walletAddress" label={translate('module.redpacket.rewardAddressCol')} />
          <TextField source="amount" label={translate('module.redpacket.amount')} />
          <FunctionField
            label={translate('module.redpacket.type')}
            render={(record: RedPacketRecord) => (
              <Chip label={getTypeName(record.type)} size="small" />
            )}
          />
          <FunctionField
            label={translate('module.redpacket.fundPool')}
            render={(record: RedPacketRecord) => {
              // 根据类型判断资金池
              const poolMap: Record<RedPacketType, string> = {
                [RedPacketType.TWITTER_BINDING]: 'Shared POOL',
                [RedPacketType.TWITTER_FOLLOW]: 'Shared POOL',
                [RedPacketType.DEPOSIT]: 'Shared POOL',
                [RedPacketType.TRADING]: 'Shared POOL',
                [RedPacketType.CRITICAL_HIT]: 'Critical Hit POOL',
              };
              return poolMap[record.type] || '-';
            }}
          />
          <FunctionField
            label={translate('module.redpacket.status')}
            render={(record: RedPacketRecord) => (
              <Chip label={getStatusName(record.status)} size="small" />
            )}
          />
          <FunctionField
            label={translate('module.redpacket.createdAtCol')}
            render={(record: RedPacketRecord) => {
              const date = record.createdAt;
              return date ? new Date(date).toLocaleString() : '-';
            }}
          />
        </Datagrid>
      </Box>
      <TablePagination
        component="div"
        count={total}
        page={pagination.page - 1}
        onPageChange={(_event, newPage) =>
          setPagination((prev) => ({ ...prev, page: newPage + 1 }))
        }
        rowsPerPage={pagination.perPage}
        onRowsPerPageChange={(event) =>
          setPagination({ page: 1, perPage: parseInt(event.target.value, 10) || 10 })
        }
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Card>
  );
};

export default RecordsForm;
